import axios from "axios";
import { AuthMethod } from "@/auth/authMethod";
import type { SignOutReason } from "@/auth/signOut";
import { config } from "@/config";

export interface TokenResponse {
    accessToken: string;
    expiresIn: number;
}

const TOKEN_PATH = "/api/token";
const REVOKE_PATH = "/api/token/revoke";

// We have a single Amplify app for all envs. The Amplify rewrite rules match on path alone — we add the env as a
// suffix so we can add env specific redirect rules. the refresh cookie is scoped to /api/token so we can add suffixes
// to that path while still keeping the cookie valid
const getProxiedPath = (path: string) =>
    config.oauth.proxyEnvironment ? `${path}/${config.oauth.proxyEnvironment}` : path;

export class TokenEndpointError extends Error {
    readonly status: number;
    readonly code?: string;

    constructor(status: number, code?: string) {
        super(`Token endpoint answered ${status}${code ? ` ${code}` : ""}`);
        this.name = "TokenEndpointError";
        this.status = status;
        this.code = code;
    }
}

// Same-origin, or the browser drops the `SameSite=Strict` refresh cookie the token endpoint sets. `keepalive` lets
// the request finish after a reload or navigation, so the rotated cookie is stored instead of discarded.
const postToTokenEndpoint = async (path: string, body: URLSearchParams): Promise<Response> => {
    const response = await fetch(`${window.location.origin}${getProxiedPath(path)}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
        keepalive: true,
    });

    if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: unknown };
        throw new TokenEndpointError(response.status, typeof data.error === "string" ? data.error : undefined);
    }

    return response;
};

const parseTokenResponse = (data: { access_token?: string; expires_in?: number }): TokenResponse => {
    const { access_token: accessToken, expires_in: expiresIn } = data;
    if (!accessToken || !expiresIn) {
        throw new Error("Token response is missing access_token or expires_in");
    }

    return { accessToken, expiresIn };
};

interface ExchangeParams {
    code: string;
    codeVerifier: string;
    redirectUri: string;
}

export const exchangeAuthorizationCode = async ({
    code,
    codeVerifier,
    redirectUri,
}: ExchangeParams): Promise<TokenResponse> => {
    const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: config.oauth.clientId,
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
    });
    const response = await postToTokenEndpoint(TOKEN_PATH, body);

    return parseTokenResponse(await response.json());
};

export const renewAccessToken = async (): Promise<TokenResponse> => {
    const body = new URLSearchParams({ grant_type: "refresh_token", client_id: config.oauth.clientId });
    const response = await postToTokenEndpoint(TOKEN_PATH, body);

    return parseTokenResponse(await response.json());
};

// A renewal fails either because the server refused the grant or because the request never landed.
// Only the first means the session is over: any other error must leave it in place.
const REJECTED_GRANT_ERRORS = new Set([
    "invalid_grant", // the refresh token is revoked, expired, or superseded past the server's grace window
    "invalid_request", // no refresh cookie rode with the request, so there was no grant to present
]);

export const isRejectedGrant = (error: unknown): boolean =>
    error instanceof TokenEndpointError && error.code !== undefined && REJECTED_GRANT_ERRORS.has(error.code);

export const revokeSession = async (): Promise<void> => {
    const body = new URLSearchParams({ client_id: config.oauth.clientId });
    await postToTokenEndpoint(REVOKE_PATH, body);
};

export const fetchAuthMethod = async (): Promise<AuthMethod> => {
    try {
        const { data } = await axios.get<{ method?: string }>(`${config.xola.url}/api/authMethod`);
        return data.method === AuthMethod.OAUTH ? AuthMethod.OAUTH : AuthMethod.API_KEY;
    } catch {
        return AuthMethod.API_KEY;
    }
};

interface AuthorizeUrlParams {
    codeChallenge: string;
    state: string;
    redirectUri: string;
    reason?: SignOutReason;
}

export const buildAuthorizeUrl = ({ codeChallenge, state, redirectUri, reason }: AuthorizeUrlParams): string => {
    const query = new URLSearchParams({
        response_type: "code",
        client_id: config.oauth.clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        state,
    });
    if (reason) {
        query.set("reason", reason);
    }

    return `${config.xola.url}/api/authorize?${query.toString()}`;
};
